import { Select } from 'antd';
import React from 'react';

import './file-interaction-icon.scss';

export const FILE_INTERACTION_ICON = {
  BOOK: 'book',
  SEARCH: 'search',
  MEMORY: 'memory',
  PUZZLE: 'puzzle',
  GAME: 'game',
  LISTEN: 'listen',
  WRITE: 'write',
  SPEAK: 'speak',
  TARGET: 'target',
  IDEA: 'idea',
} as const;

export type FileInteractionIconName = (typeof FILE_INTERACTION_ICON)[keyof typeof FILE_INTERACTION_ICON];

const ICON_NAMES = new Set<string>(Object.values(FILE_INTERACTION_ICON));


export const FILE_INTERACTION_ICON_TONES = [
  { background: '#F0E8FF', border: '#D9C9F6', foreground: '#7452BD' },
  { background: '#E8F2FF', border: '#C9DDF7', foreground: '#5279B8' },
  { background: '#E8F8F2', border: '#C8E9DC', foreground: '#438D78' },
  { background: '#FFF0E7', border: '#F2D5C4', foreground: '#BB7354' },
  { background: '#FDEAF2', border: '#EECBDD', foreground: '#B55F82' },
  { background: '#ECECFF', border: '#D1D2F5', foreground: '#656BB5' },
  { background: '#E8F7FA', border: '#C8E7EC', foreground: '#4E8D99' },
  { background: '#FFF5DF', border: '#ECDDBA', foreground: '#9B773E' },
  { background: '#F5EAF9', border: '#E2CDEB', foreground: '#895AA0' },
  { background: '#EDF7E8', border: '#D4E8C9', foreground: '#668F50' },
] as const;

export const getFileInteractionIconTone = (toneIndex?: number | null) => {
  if (typeof toneIndex !== 'number' || !Number.isFinite(toneIndex)) {
    return FILE_INTERACTION_ICON_TONES[0];
  }

  const normalizedIndex = ((Math.trunc(toneIndex) % FILE_INTERACTION_ICON_TONES.length) + FILE_INTERACTION_ICON_TONES.length) % FILE_INTERACTION_ICON_TONES.length;

  return FILE_INTERACTION_ICON_TONES[normalizedIndex];
};

const iconContent = (name: FileInteractionIconName) => {
  switch (name) {
    case FILE_INTERACTION_ICON.BOOK:
      return (
        <>
          <path d="M13 15.5c4.4-1.4 7.5-.8 11 2.2v16.1c-3.6-2.8-6.8-3.4-11-2V15.5Z" />
          <path d="M35 15.5c-4.4-1.4-7.5-.8-11 2.2v16.1c3.6-2.8 6.8-3.4 11-2V15.5Z" />
          <path d="M17 20.2c2-.3 3.7.1 5.3 1.2M31 20.2c-2-.3-3.7.1-5.3 1.2" />
        </>
      );
    case FILE_INTERACTION_ICON.SEARCH:
      return (
        <>
          <circle cx="21" cy="21" r="8" />
          <path d="m27 27 8 8" />
          <path d="M17.5 21h7M21 17.5v7" opacity=".42" />
        </>
      );
    case FILE_INTERACTION_ICON.MEMORY:
      return (
        <>
          <path d="M18 15.5a5 5 0 0 0-4 8.1 5.4 5.4 0 0 0 2.7 9.8c.5 3 4.9 3.8 7.3 1.4V14.7a4.7 4.7 0 0 0-6 0v.8Z" />
          <path d="M30 15.5a5 5 0 0 1 4 8.1 5.4 5.4 0 0 1-2.7 9.8c-.5 3-4.9 3.8-7.3 1.4V14.7a4.7 4.7 0 0 1 6 0v.8Z" />
          <path d="M18 23h3M27 23h3M18.5 29.5h2.5M27 29.5h2.5" />
        </>
      );
    case FILE_INTERACTION_ICON.PUZZLE:
      return (
        <path d="M13.5 16.5h8.3c-.2-3.8 5.7-3.8 5.4 0h7.3v7.4c3.9-.2 3.9 5.6 0 5.4v7.2h-7.4c.3-3.8-5.6-3.8-5.3 0h-8.3v-7.3c-3.8.3-3.8-5.7 0-5.4v-7.3Z" />
      );
    case FILE_INTERACTION_ICON.GAME:
      return (
        <>
          <path d="M17 20.5h14c3.5 0 6.1 7.2 5.7 11.3-.2 2.4-2.7 3.3-4.4 1.5l-3.2-3.4H18.9l-3.2 3.4c-1.7 1.8-4.2.9-4.4-1.5-.4-4.1 2.2-11.3 5.7-11.3Z" />
          <path d="M18.5 24v5M16 26.5h5" />
          <circle cx="29.5" cy="25" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="32.5" cy="28" r="1.2" fill="currentColor" stroke="none" />
        </>
      );
    case FILE_INTERACTION_ICON.LISTEN:
      return (
        <>
          <path d="M14 26v-3c0-6 4.5-10 10-10s10 4 10 10v3" />
          <path d="M14 25.5h3.5v8H15c-1.7 0-3-1.3-3-3v-2c0-1.7.8-3 2-3ZM34 25.5h-3.5v8H33c1.7 0 3-1.3 3-3v-2c0-1.7-.8-3-2-3Z" />
          <path d="M30.5 34c-1.4 1.2-3.3 1.8-5.6 1.8" />
        </>
      );
    case FILE_INTERACTION_ICON.WRITE:
      return (
        <>
          <path d="m15 32 1.6-6.3L29.8 12.5l5.7 5.7-13.2 13.2L16 33l-1-1Z" />
          <path d="m27.8 14.5 5.7 5.7M16.8 26l5.2 5.2" />
          <path d="M13 36h22" />
        </>
      );
    case FILE_INTERACTION_ICON.SPEAK:
      return (
        <>
          <path d="M13 16.5h22v15H23l-6.5 5v-5H13v-15Z" />
          <circle cx="19" cy="24" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="24" cy="24" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="29" cy="24" r="1.2" fill="currentColor" stroke="none" />
        </>
      );
    case FILE_INTERACTION_ICON.TARGET:
      return (
        <>
          <circle cx="23" cy="25" r="11" />
          <circle cx="23" cy="25" r="6" />
          <circle cx="23" cy="25" r="1.7" fill="currentColor" stroke="none" />
          <path d="m25 23 9-9M30 14h4v4" />
        </>
      );
    case FILE_INTERACTION_ICON.IDEA:
      return (
        <>
          <path d="M24 12.5a9.5 9.5 0 0 0-5.8 17c1.4 1.1 2 2.2 2.1 3.5h7.4c.1-1.3.7-2.4 2.1-3.5a9.5 9.5 0 0 0-5.8-17Z" />
          <path d="M20.5 36h7M21.5 39h5" />
          <path d="M24 8v2M12.5 13l1.8 1.8M35.5 13l-1.8 1.8M9 24h2.3M36.7 24H39" />
        </>
      );
    default:
      return null;
  }
};

export const isFileInteractionIconName = (value: unknown): value is FileInteractionIconName =>
  typeof value === 'string' && ICON_NAMES.has(value);

export const FileInteractionIcon = ({
  name,
  size = 36,
  className,
  toneIndex,
}: {
  name?: string | null;
  size?: number;
  className?: string;
  toneIndex?: number | null;
}) => {
  if (!isFileInteractionIconName(name)) return null;

  const tone = getFileInteractionIconTone(toneIndex);

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="24" cy="24" r="23" fill={tone.background} />
      <circle cx="24" cy="24" r="22.25" stroke={tone.border} strokeWidth="1.5" />
      <g
        color={tone.foreground}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {iconContent(name)}
      </g>
    </svg>
  );
};

const localizedNames = (lng?: string | null): Record<FileInteractionIconName, string> => {
  if (lng === 'ru') {
    return {
      book: 'Чтение', search: 'Поиск', memory: 'Память', puzzle: 'Логика', game: 'Игра',
      listen: 'Аудирование', write: 'Письмо', speak: 'Говорение', target: 'Практика', idea: 'Идея',
    };
  }

  if (lng === 'en') {
    return {
      book: 'Reading', search: 'Search', memory: 'Memory', puzzle: 'Logic', game: 'Game',
      listen: 'Listening', write: 'Writing', speak: 'Speaking', target: 'Practice', idea: 'Idea',
    };
  }

  return {
    book: 'Читання', search: 'Пошук', memory: 'Памʼять', puzzle: 'Логіка', game: 'Гра',
    listen: 'Аудіювання', write: 'Письмо', speak: 'Говоріння', target: 'Практика', idea: 'Ідея',
  };
};

export const getFileInteractionIconCopy = (lng?: string | null) => ({
  label: lng === 'ru' ? 'Иконка файла' : lng === 'en' ? 'File icon' : 'Іконка файлу',
  placeholder: lng === 'ru' ? 'Без иконки' : lng === 'en' ? 'No icon' : 'Без іконки',
});

export const getFileInteractionIconOptions = (lng?: string | null) => {
  const labels = localizedNames(lng);

  return Object.values(FILE_INTERACTION_ICON).map((value) => ({
    value,
    label: (
      <span className="file-interaction-icon__option">
        <FileInteractionIcon name={value} size={28} />
        <span>{labels[value]}</span>
      </span>
    ),
  }));
};

export const FileInteractionIconSelector = ({
  value,
  onChange,
  lng,
  disabled = false,
}: {
  value?: string | null;
  onChange?: (value: string | null) => void;
  lng?: string | null;
  disabled?: boolean;
}) => {
  const copy = getFileInteractionIconCopy(lng);

  return (
    <Select
      className="file-interaction-icon__selector"
      value={isFileInteractionIconName(value) ? value : undefined}
      options={getFileInteractionIconOptions(lng)}
      placeholder={copy.placeholder}
      allowClear
      disabled={disabled}
      onChange={(nextValue) => onChange?.(nextValue ?? null)}
    />
  );
};

export default FileInteractionIcon;
